import { DisclosureParagraph } from '@/components/common/DisclosureParagraph';
import { FlexRow } from '@/components/common/Flex';
import { signOut } from '@/services/stytch';
import { useUserData } from '@/services/user';
import {
    FlexProps,
    Link,
    LinkProps,
    ListItem,
    UnorderedList,
} from '@chakra-ui/react';
import {
    ABOUT_US_LINK,
    HELP_CENTER_LINK,
    LEGAL_LINK,
} from '@common/compliance';
import { useCurrentRateInfo } from '@common/react/rates';
import { useRouter } from 'next/router';
import { FlexCol } from '../common/Flex';
import PlentyLogo from '../common/PlentyLogo';
import GeneralDisclosuresLink from './GeneralDisclosuresLink';

export interface FooterProps extends FlexProps {
    showInteractiveToolDisclosure?: boolean;
}

export default function Footer({
    showInteractiveToolDisclosure = true,
    ...props
}: FooterProps) {
    const { currentRateInfo } = useCurrentRateInfo();
    if (!currentRateInfo) return null;

    return (
        <FlexCol mt={8} pt={4} pb={8} w="full" {...props}>
            <FlexRow mb={8} align="center" justify="space-between">
                <PlentyLogo width={140} textPosition="horizontal" />
                <FlexRow gap={{ base: 3, lg: 8 }}>
                    <FooterLinks fontSize="sm" fontWeight="light" />
                </FlexRow>
            </FlexRow>
            <DisclosureParagraph>
                Plenty was founded to democratize access to financial products
                and tools that accelerate wealth building. Plenty Financial,
                Inc. (“Plenty Financial”) provides a web, mobile, personal
                financial management and educational platform to consumers; some
                of these tools are freely accessible to all consumers, others
                require a fee-based subscription. Wholly owned subsidiary Plenty
                Financial RIA, LLC (“Plenty RIA”) is an SEC-registered
                investment advisor that provides advisory services (“Advisory
                Services” or “Services”) in the form of software-first financial
                planning and investment advisory services for fee-based
                Subscribers. Registration as an investment adviser does not
                imply a certain level of skill or training.
            </DisclosureParagraph>
            <DisclosureParagraph>
                Investing is inherently risky and Plenty Financial does not
                guarantee positive performance. Investors may experience
                possible loss of principal. This is not a recommendation for any
                security or investment. References to any investments are
                intended to illustrate the application of the investment process
                only and should not be used as the basis for making any decision
                about purchasing, holding or selling any securities. This
                Interactive Analysis Tool contains inherent limitations and is
                not a substitute for actual investment returns derived from a
                live portfolio. There are numerous risk factors related to
                trading and markets which cannot be fully accounted for when
                modeling hypothetical future performance. Consequently, future
                returns are not guaranteed, and a loss of principal may occur;
                results may vary with each use and over time. Please see
                Plenty&apos;s <GeneralDisclosuresLink />.
            </DisclosureParagraph>
            {showInteractiveToolDisclosure && (
                <>
                    <DisclosureParagraph>
                        Return data used in the interactive tool are
                        hypothetical performance data which contains inherent
                        limitations and is not a substitute for actual
                        investment returns derived from a live portfolio. There
                        are numerous risk factors related to trading and markets
                        which cannot be fully accounted for when depicting
                        Hypothetical Performance. Consequently, future returns
                        are not guaranteed, and a loss of principal may occur.
                    </DisclosureParagraph>
                    <DisclosureParagraph>
                        The Tool depicted assumes the following:
                    </DisclosureParagraph>
                    <UnorderedList
                        pl={2}
                        fontSize="sm"
                        color="gray.400"
                        fontWeight="light"
                    >
                        <ListItem>
                            A steady contribution rate (set by you)
                        </ListItem>
                        <ListItem>
                            An annual rate of return of x% per year (based on a
                            risk level you select)
                        </ListItem>
                        <ListItem>
                            Including a reinvestment of interest and dividends
                        </ListItem>
                        <ListItem>
                            Excluding taxes, transaction fees, management fees,
                            and other factors which might impact results
                        </ListItem>
                    </UnorderedList>
                </>
            )}
        </FlexCol>
    );
}

export function FooterLinks(linkProps: LinkProps) {
    const { stytchUser, userInfo } = useUserData();
    const router = useRouter();

    return (
        <>
            <Link
                {...linkProps}
                href={ABOUT_US_LINK}
                target="_blank"
                tabIndex={-1}
                whiteSpace="nowrap"
            >
                About us
            </Link>
            <Link
                {...linkProps}
                href={HELP_CENTER_LINK}
                target="_blank"
                tabIndex={-1}
                whiteSpace="nowrap"
            >
                Help center
            </Link>
            <Link
                {...linkProps}
                href={LEGAL_LINK}
                target="_blank"
                tabIndex={-1}
            >
                Legal
            </Link>
            {stytchUser && userInfo && (
                <Link
                    {...linkProps}
                    onClick={async () => {
                        await signOut();
                        router.push('/');
                    }}
                    tabIndex={-1}
                    whiteSpace="nowrap"
                >
                    Sign out
                </Link>
            )}
        </>
    );
}
